import React, { useState } from 'react';
import { motion } from 'framer-motion';
import StarCanvas from './StarCanvas';
import { ArrowLeft, ArrowRight, CheckCircle, Loader } from 'lucide-react';
import { useWaitlist } from '@/context/WaitlistContext';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  company: string;
  companySize: string;
  industry: string;
  jobTitle: string;
  primaryPainPoint: string;
  currentMonthlyOutreach: string;
}

const INITIAL_FORM_DATA: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  company: '',
  companySize: '',
  industry: '',
  jobTitle: '',
  primaryPainPoint: '',
  currentMonthlyOutreach: ''
};

export const BookingForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { registerSpot, isRegistered, spotsLeft } = useWaitlist();

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.firstName && formData.lastName && formData.email;
      case 2:
        return formData.company && formData.companySize && formData.industry;
      case 3:
        return formData.jobTitle && formData.primaryPainPoint && formData.currentMonthlyOutreach;
      default:
        return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      setShowError(true);
      setErrorMessage('Email is required');
      return;
    }

    setIsSubmitting(true);
    setShowError(false);
    setShowSuccess(false);

    try {
      console.log('Attempting registration with:', formData);
      console.log('Current spots left:', spotsLeft);
      
      const spotRegistered = await registerSpot(
        formData.email,
        `${formData.firstName} ${formData.lastName}`,
        formData.company
      );
      console.log('Registration result:', spotRegistered);
      
      if (!spotRegistered) {
        setShowError(true);
        if (isRegistered(formData.email)) {
          setErrorMessage("This email is already registered.");
        } else {
          setErrorMessage('Failed to register. Please try again.');
        }
        return;
      }

      // Submit to Loops API
      const formBody = `userGroup=ACOS%20Waitlist&mailingLists=&email=${encodeURIComponent(formData.email)}&firstName=${encodeURIComponent(formData.firstName)}&lastName=${encodeURIComponent(formData.lastName)}&phoneNumber=${encodeURIComponent(formData.phoneNumber)}&company=${encodeURIComponent(formData.company)}&companySize=${encodeURIComponent(formData.companySize)}&industry=${encodeURIComponent(formData.industry)}&jobTitle=${encodeURIComponent(formData.jobTitle)}&primaryPainPoint=${encodeURIComponent(formData.primaryPainPoint)}&currentMonthlyOutreach=${encodeURIComponent(formData.currentMonthlyOutreach)}`;

      const response = await fetch('https://app.loops.so/api/newsletter-form/cm1ig6dn900lexiha3rutprp9', {
        method: 'POST',
        body: formBody,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        console.error('Loops API error:', data);
        // Still show success since the registration worked
        setShowSuccess(true);
        setFormData(INITIAL_FORM_DATA);
      } else {
        setShowSuccess(true);
        setFormData(INITIAL_FORM_DATA);
      }
      
      console.log('Registration complete. New spots left:', spotsLeft);
      
    } catch (error) {
      console.error('Submission error:', error);
      setShowError(true);
      setErrorMessage('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <section className="py-24 relative overflow-hidden">
        <StarCanvas />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-50" />
        
        <div className="max-w-xl mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-fuchsia-600/30 rounded-2xl blur-xl" />
            
            <div className="relative bg-black/40 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/20">
              <div className="text-center">
                {/* Success Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-full mx-auto mb-6 flex items-center justify-center"
                >
                  <CheckCircle className="w-10 h-10 text-white" />
                </motion.div>

                {/* Success Title */}
                <motion.h3 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-3xl font-bold text-white mb-4"
                >
                  You're In! 🎉
                </motion.h3>

                {/* Success Message */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="space-y-3"
                >
                  <p className="text-xl text-purple-200">
                    Check your inbox (and spam folder)
                  </p>
                  <p className="text-gray-400">
                    We've sent you a special welcome guide and next steps.
                  </p>
                </motion.div>

                {/* Back Button */}
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  onClick={() => {
                    setShowSuccess(false);
                    setStep(1);
                    setFormData(INITIAL_FORM_DATA);
                  }}
                  className="mt-8 text-purple-400 hover:text-purple-300 text-sm flex items-center gap-2 mx-auto transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Return to form</span>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 relative overflow-hidden">
      <StarCanvas />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-50" />

      <div id="contact-form" className="max-w-xl mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-purple-400/20 rounded-xl blur-xl opacity-50" />
          
          <div className="relative bg-black/40 backdrop-blur-sm rounded-xl p-8 border border-purple-500/10">
            <div className="text-center mb-8">
              <motion.h2 
                className="text-2xl md:text-3xl font-bold text-white mb-4"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Join the Waitlist
              </motion.h2>
              <motion.p 
                className="text-gray-400"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                Be among the first to experience ACOS
              </motion.p>
            </div>

            {showError && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-center">{errorMessage}</p>
              </div>
            )}

            <div className="flex gap-2 mb-8">
              {[1, 2, 3].map((stepNum) => (
                <motion.div 
                  key={stepNum}
                  className="flex-1"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: stepNum * 0.1 }}
                >
                  <div
                    className={`h-1 rounded-full transition-colors duration-300 ${
                      step >= stepNum ? 'bg-purple-500' : 'bg-gray-700'
                    }`}
                  />
                </motion.div>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="relative">
              {/* Step 1: Personal Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: step === 1 ? 1 : 0, x: step === 1 ? 0 : 20 }}
                className={`space-y-6 ${step === 1 ? 'block' : 'hidden'}`}
              >
                <div>
                  <label className="block text-gray-300 mb-2">First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Your first name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Your last name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="you@company.com"
                  />
                </div>
              </motion.div>

              {/* Step 2: Company Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: step === 2 ? 1 : 0, x: step === 2 ? 0 : 20 }}
                className={`space-y-6 ${step === 2 ? 'block' : 'hidden'}`}
              >
                <div>
                  <label className="block text-gray-300 mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Company Size</label>
                  <select
                    name="companySize"
                    required
                    value={formData.companySize}
                    onChange={(e) => setFormData(prev => ({ ...prev, companySize: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select size</option>
                    <option value="1-10">1-10 employees</option>
                    <option value="11-50">11-50 employees</option>
                    <option value="51-200">51-200 employees</option>
                    <option value="201-500">201-500 employees</option>
                    <option value="500+">500+ employees</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Industry</label>
                  <select
                    name="industry"
                    required
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select industry</option>
                    <option value="saas">SaaS/Technology</option>
                    <option value="services">Professional Services</option>
                    <option value="finance">Finance/Insurance</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </motion.div>

              {/* Step 3: Role Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: step === 3 ? 1 : 0, x: step === 3 ? 0 : 20 }}
                className={`space-y-6 ${step === 3 ? 'block' : 'hidden'}`}
              >
                <div>
                  <label className="block text-gray-300 mb-2">Job Title</label>
                  <input
                    type="text"
                    name="jobTitle"
                    required
                    value={formData.jobTitle}
                    onChange={(e) => setFormData(prev => ({ ...prev, jobTitle: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                    placeholder="Your role"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Primary Pain Point</label>
                  <select
                    name="primaryPainPoint"
                    required
                    value={formData.primaryPainPoint}
                    onChange={(e) => setFormData(prev => ({ ...prev, primaryPainPoint: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select pain point</option>
                    <option value="low_response_rates">Low Response Rates</option>
                    <option value="manual_prospecting">Time-Consuming Manual Prospecting</option>
                    <option value="lead_quality">Poor Lead Quality</option>
                    <option value="scaling_outreach">Difficulty Scaling Outreach</option>
                    <option value="tool_complexity">Too Many Tools/Complex Stack</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-300 mb-2">Current Monthly Outreach Volume</label>
                  <select
                    name="currentMonthlyOutreach"
                    required
                    value={formData.currentMonthlyOutreach}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentMonthlyOutreach: e.target.value }))}
                    className="w-full bg-black/40 border border-purple-500/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple-500 transition-colors"
                  >
                    <option value="">Select volume</option>
                    <option value="<1000">Less than 1,000 emails</option>
                    <option value="1000-5000">1,000-5,000 emails</option>
                    <option value="5000-20000">5,000-20,000 emails</option>
                    <option value="20000+">20,000+ emails</option>
                  </select>
                </div>
              </motion.div>

              {/* Navigation */}
              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <motion.button
                    type="button"
                    onClick={() => setStep(step - 1)}
                    className="flex items-center gap-2 px-6 py-2 text-gray-400 hover:text-white transition-colors group"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                    Back
                  </motion.button>
                )}
                {step < 3 ? (
                  <motion.button
                    type="button"
                    onClick={() => isStepValid() && setStep(step + 1)}
                    className={`ml-auto flex items-center gap-2 px-8 py-3 bg-purple-600 text-white font-semibold rounded-full transition-all ${
                      isStepValid() 
                        ? 'hover:bg-purple-500' 
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </motion.button>
                ) : (
                  <motion.button
                    type="submit"
                    disabled={isSubmitting || !isStepValid()}
                    className={`ml-auto flex items-center gap-2 px-8 py-3 bg-purple-600 text-white font-semibold rounded-full transition-all ${
                      isStepValid() && !isSubmitting
                        ? 'hover:bg-purple-500'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="w-4 h-4 animate-spin" />
                        Please wait...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Join Waitlist
                      </>
                    )}
                  </motion.button>
                )}
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </section>
  );
}; 